import { useEffect, useState, useCallback } from 'react';
import './Toast.css';

// Toast — for simple notifications (replaces alert())
// Props: message, type ('success'|'error'|'warning'|'info'), onClose
export function Toast({ message, type = 'info', onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  const icons = { success: '✅', error: '❌', warning: '⚠️', info: 'ℹ️' };

  return (
    <div className={`toast-overlay`}>
      <div className={`toast-box toast-${type}`}>
        <span className="toast-icon">{icons[type]}</span>
        <p className="toast-message">{message}</p>
        <button className="toast-close" onClick={onClose}>✕</button>
      </div>
    </div>
  );
}

// ConfirmModal — replaces window.confirm()
// Props: message, title, confirmLabel, onConfirm, onCancel
export function ConfirmModal({ title = 'Are you sure?', message, confirmLabel = 'Confirm', danger = false, onConfirm, onCancel }) {
  return (
    <div className="toast-overlay">
      <div className="confirm-box">
        <div className={`confirm-icon-wrap ${danger ? 'danger' : ''}`}>
          {danger ? '🗑️' : '❓'}
        </div>
        <h3 className="confirm-title">{title}</h3>
        {message && <p className="confirm-message">{message}</p>}
        <div className="confirm-actions">
          <button className="btn-confirm-cancel" onClick={onCancel}>Cancel</button>
          <button className={`btn-confirm-ok ${danger ? 'danger' : ''}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// useToast hook — manage toast state in any component

export function useToast() {
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
  }, []);

  const hideToast = useCallback(() => setToast(null), []);

  const ToastComponent = toast
    ? <Toast message={toast.message} type={toast.type} onClose={hideToast} />
    : null;

  return { showToast, ToastComponent };
}

// useConfirm hook — manage confirm modal state
export function useConfirm() {
  const [confirmState, setConfirmState] = useState(null);

  const showConfirm = useCallback(({ title, message, confirmLabel, danger, onConfirm }) => {
    setConfirmState({ title, message, confirmLabel, danger, onConfirm });
  }, []);

  const handleConfirm = () => {
    confirmState?.onConfirm();
    setConfirmState(null);
  };

  const handleCancel = () => setConfirmState(null);

  const ConfirmComponent = confirmState
    ? <ConfirmModal
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        danger={confirmState.danger}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    : null;

  return { showConfirm, ConfirmComponent };
}
